import { createContext, type FC, type ReactElement, useState } from 'react';
import type {
  CommentModel,
  GeneralBlock,
  IFaultsBlock,
  IGlassWashersBlock,
  IHygieneBlock,
  IWastageBlock,
  PreCleanBlock,
  TemperaturesBlock
} from '../models';

interface IVisitDashboardContext {
  dashboardsUpdate: boolean;
  toggleDashboardsUpdate: () => void;
  loading: boolean;
  setLoading: (val: boolean) => void;

  comments: CommentModel[] | null;
  setComments: (v: CommentModel[]) => void;

  generalBlockProData: GeneralBlock | null | undefined;
  setGeneralBlockProData: (v: GeneralBlock | null | undefined) => void;
  generalBlockChillCellarData: GeneralBlock | null | undefined;
  setGeneralBlockChillCellarData: (v: GeneralBlock | null | undefined) => void;
  generalBlockWinData: GeneralBlock | null | undefined;
  setGeneralBlockWinData: (v: GeneralBlock | null | undefined) => void;
  generalBlockDewinData: GeneralBlock | null | undefined;
  setGeneralBlockDewinData: (v: GeneralBlock | null | undefined) => void;
  generalBlockSOS: GeneralBlock | null | undefined;
  setGeneralBlockSOS: (v: GeneralBlock | undefined) => void;

  preCleanBlock: PreCleanBlock | null;
  setPreCleanBlock: (v: PreCleanBlock) => void;
  preCleanWithTaintBlock: PreCleanBlock | null | undefined;
  setPreCleanWithTaintBlock: (v: PreCleanBlock | undefined) => void;

  temperaturesBlock: TemperaturesBlock | null | undefined;
  setTemperaturesBlock: (v: TemperaturesBlock | null | undefined) => void;

  glassWashersBlock: IGlassWashersBlock | null;
  setGlassWashersBlock: (v: IGlassWashersBlock) => void;

  hygieneBlockProClean: IHygieneBlock | null | undefined;
  setHygieneBlockProClean: (v: IHygieneBlock | null | undefined) => void;
  hygieneBlockCellarPlus: IHygieneBlock | null | undefined;
  setHygieneBlockCellarPlus: (v: IHygieneBlock | null | undefined) => void;
  hygieneBlockChillPour: IHygieneBlock | null | undefined;
  setHygieneBlockChillPour: (v: IHygieneBlock | null | undefined) => void;
  hygieneBlockWinDewin: IHygieneBlock | null | undefined;
  setHygieneBlockWinDewin: (v: IHygieneBlock | undefined | null) => void;
  hygieneBlockSOS: IHygieneBlock | null | undefined;
  setHygieneBlockSOS: (v: IHygieneBlock | null | undefined) => void;
  hygieneBlockSOS2: IHygieneBlock | null | undefined;
  setHygieneBlockSOS2: (v: IHygieneBlock | null | undefined) => void;

  wastageBlockBase: IWastageBlock | null | undefined;
  setWastageBlockBase: (v: IWastageBlock | null | undefined) => void;
  wastageBlockFull: IWastageBlock | null | undefined;
  setWastageBlockFull: (v: IWastageBlock | null | undefined) => void;

  faultsBlock: IFaultsBlock | null;
  setFaultsBlock: (v: IFaultsBlock) => void;
}

export const VisitDashboardContext = createContext<IVisitDashboardContext>({} as IVisitDashboardContext);

export const VisitDashboardProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [dashboardsUpdate, setDashboardsUpdate] = useState(false);

  const [comments, setComments] = useState<CommentModel[] | null>(null);

  const [generalBlockProData, setGeneralBlockProData] = useState<GeneralBlock | null | undefined>(null);
  const [generalBlockChillCellarData, setGeneralBlockChillCellarData] = useState<GeneralBlock | null | undefined>(null);
  const [generalBlockWinData, setGeneralBlockWinData] = useState<GeneralBlock | null | undefined>(null);
  const [generalBlockDewinData, setGeneralBlockDewinData] = useState<GeneralBlock | null | undefined>(null);
  const [generalBlockSOS, setGeneralBlockSOS] = useState<GeneralBlock | null | undefined>(null);

  const [preCleanBlock, setPreCleanBlock] = useState<PreCleanBlock | null>(null);
  const [preCleanWithTaintBlock, setPreCleanWithTaintBlock] = useState<PreCleanBlock | null | undefined>(null);

  const [temperaturesBlock, setTemperaturesBlock] = useState<TemperaturesBlock | null | undefined>(null);

  const [glassWashersBlock, setGlassWashersBlock] = useState<IGlassWashersBlock | null>(null);

  const [hygieneBlockProClean, setHygieneBlockProClean] = useState<IHygieneBlock | null | undefined>(null);
  const [hygieneBlockCellarPlus, setHygieneBlockCellarPlus] = useState<IHygieneBlock | null | undefined>(null);
  const [hygieneBlockChillPour, setHygieneBlockChillPour] = useState<IHygieneBlock | null | undefined>(null);
  const [hygieneBlockWinDewin, setHygieneBlockWinDewin] = useState<IHygieneBlock | null | undefined>(null);
  const [hygieneBlockSOS, setHygieneBlockSOS] = useState<IHygieneBlock | null | undefined>(null);
  const [hygieneBlockSOS2, setHygieneBlockSOS2] = useState<IHygieneBlock | null | undefined>(null);

  const [wastageBlockFull, setWastageBlockFull] = useState<IWastageBlock | null | undefined>(null);
  const [wastageBlockBase, setWastageBlockBase] = useState<IWastageBlock | null | undefined>(null);

  const [faultsBlock, setFaultsBlock] = useState<IFaultsBlock | null>(null);

  const [loading, setLoading] = useState(false);

  const toggleUpdate = (): void => setDashboardsUpdate(!dashboardsUpdate);

  return (
    <VisitDashboardContext.Provider
      value={{
        dashboardsUpdate,
        toggleDashboardsUpdate: toggleUpdate,
        comments,
        setComments,
        generalBlockProData,
        setGeneralBlockProData,
        generalBlockChillCellarData,
        setGeneralBlockChillCellarData,
        generalBlockWinData,
        setGeneralBlockWinData,
        generalBlockDewinData,
        generalBlockSOS,
        setGeneralBlockSOS,
        setGeneralBlockDewinData,
        preCleanBlock,
        setPreCleanBlock,
        preCleanWithTaintBlock,
        setPreCleanWithTaintBlock,
        temperaturesBlock,
        setTemperaturesBlock,
        glassWashersBlock,
        setGlassWashersBlock,
        hygieneBlockProClean,
        setHygieneBlockProClean,
        hygieneBlockCellarPlus,
        setHygieneBlockCellarPlus,
        hygieneBlockChillPour,
        setHygieneBlockChillPour,
        hygieneBlockWinDewin,
        setHygieneBlockWinDewin,
        hygieneBlockSOS,
        setHygieneBlockSOS,
        hygieneBlockSOS2,
        setHygieneBlockSOS2,
        wastageBlockFull,
        setWastageBlockFull,
        wastageBlockBase,
        setWastageBlockBase,
        faultsBlock,
        setFaultsBlock,
        loading,
        setLoading
      }}
    >
      {children}
    </VisitDashboardContext.Provider>
  );
};
